/**
 * Whether this account has approved the permissions that updates and
 * mentions need, checked once when the app opens, so the alert at the top of
 * the view (App.renderPermissionAlert) says so before anyone applies.
 *
 * monday grants a new app version's permissions only after an account admin
 * approves them (its own banner: "An admin needs to review permissions for
 * this app"). Until then every call that needs them is refused with
 * "missing required scopes" (code UNAUTHORIZED_FIELD_OR_TYPE) — the same
 * refusal the apply reports for create_update (lib/captureGroup.refusedFields).
 *
 * Two probes, neither of which can change anything:
 *   reads   updates (1) and teams       → updates:read, teams:read
 *   write   create_update on item 0     → updates:write. No item 0 exists,
 *           so with the permission approved monday answers "not found";
 *           only without it does it answer "missing required scopes".
 * The write probe matters: an account may hold the read scopes from an
 * older version and miss only updates:write.
 *
 * A refusal for any other reason (network, rate limit, not found) is not a
 * missing permission: nothing is shown. What monday answered is logged as
 * [spt-permissions], to see why the alert did or did not show.
 */
import { refusedFields } from './captureGroup';

export const PROBE_QUERY = 'query { updates (limit: 1) { id } teams { id } }';
export const WRITE_PROBE = 'mutation { create_update (item_id: 0, body: "permission check") { id } }';
export const ADMINS_QUERY = 'query { users (limit: 200, kind: non_guests) { id name is_admin } }';

/** The scopes updates and mentions need: posting, reading, and who can be mentioned. */
export const FEATURE_SCOPES = Object.freeze(['updates:write', 'updates:read', 'users:read', 'teams:read']);

/**
 * The scopes the monday connection asks for (spt helpers/monday-oauth.ts
 * OAUTH_SCOPE), for copying template files. A user can only connect with
 * scopes the ACCOUNT has approved for the app: sent to connect without them,
 * monday answers "Invalid scope" and the user is stranded on its page.
 */
export const CONNECT_SCOPES = Object.freeze(['boards:write']);

/**
 * monday's view context may carry the app's scopes for this account:
 * context.permissions = { approvedScopes: [...], requiredScopes: [...] }.
 * When it does, that answers the question exactly and without a call:
 * { required, approved, missing, featureMissing, connectMissing }. null when it does not.
 */
export function scopesFromContext(context) {
  const p = context && context.permissions;
  if (!p || !Array.isArray(p.requiredScopes) || !Array.isArray(p.approvedScopes)) return null;
  const approved = new Set(p.approvedScopes.map(String));
  const missing = p.requiredScopes.map(String).filter((s) => !approved.has(s));
  return {
    required: p.requiredScopes.map(String),
    approved: [...approved],
    missing,
    featureMissing: missing.filter((s) => FEATURE_SCOPES.includes(s)),
    // against what is approved, not what is required: these must be approved to connect at all
    connectMissing: CONNECT_SCOPES.filter((s) => !approved.has(s)),
  };
}

/**
 * The connection's scopes this account has not approved: [] when it may
 * connect, null when the context does not say (connect as before).
 */
export function connectScopesMissing(context) {
  const scopes = scopesFromContext(context);
  return scopes ? scopes.connectMissing : null;
}

/**
 * Which features wait on permissions the account has not approved:
 * { updates, files }. The context's scopes when monday sends them; otherwise
 * the probes below, which only know about updates. Everything it found is
 * logged as [spt-permissions].
 */
export async function missingFeatures(api, context, log = defaultLog) {
  const scopes = scopesFromContext(context);
  if (scopes) {
    log(
      '[spt-permissions]',
      `context: missing ${scopes.missing.join(', ') || 'none'}; required ${scopes.required.join(', ')}; ` +
        `connect needs ${scopes.connectMissing.join(', ') || 'nothing more'}`,
    );
    return { updates: scopes.featureMissing.length > 0, files: scopes.connectMissing.length > 0 };
  }
  log('[spt-permissions]', 'context has no permissions; probing');
  return { updates: await permissionsMissing(api, log), files: false };
}

/** Whether the alert shows: any feature waits on a permission. */
export async function featurePermissionsMissing(api, context, log = defaultLog) {
  const m = await missingFeatures(api, context, log);
  return m.updates || m.files;
}
/** How many admins the alert names before "or another admin". */
export const ADMINS_SHOWN = 2;

async function refusedBy(api, query) {
  try {
    const res = await api.api(query);
    return refusedFields(res);
  } catch (e) {
    return [];
  }
}

/** true when monday refused a probe for a missing permission; false otherwise, including on failure. */
export async function permissionsMissing(api, log = defaultLog) {
  const reads = await refusedBy(api, PROBE_QUERY);
  const write = reads.length ? [] : await refusedBy(api, WRITE_PROBE);
  const refused = [...reads, ...write];
  log('[spt-permissions]', refused.length ? `missing: ${refused.join(', ') || 'a required scope'}` : 'all approved');
  return refused.length > 0;
}

function defaultLog(...args) {
  // eslint-disable-next-line no-console
  console.info(...args);
}

/**
 * The account's admins' names, other than `selfId`, or [] when they cannot
 * be read (users:read may be one of the permissions still waiting).
 */
export async function fetchAdminNames(api, selfId) {
  try {
    const res = await api.api(ADMINS_QUERY);
    const users = (res && res.data && res.data.users) || [];
    return users
      .filter((u) => u && u.is_admin && String(u.id) !== String(selfId) && u.name)
      .map((u) => u.name);
  } catch (e) {
    return [];
  }
}

/**
 * Local preview of the alert, so it can be seen without an account that is
 * missing permissions. REACT_APP_MOCK_PERMISSIONS (read when the dev server
 * starts; ignored in production builds):
 *   user     a non-admin, admins' names readable  → "…ask your account admin, Ann Lee or Tom Tran…"
 *   noname   a non-admin, names not readable      → "…ask an admin of your monday account…"
 *   admin    an admin                             → "…approve them in the monday banner above."
 * Returns the alert's inputs, or null when unset or unknown.
 */
export function mockPermissionAlert(env = process.env) {
  if (env.NODE_ENV === 'production') return null;
  const mode = String(env.REACT_APP_MOCK_PERMISSIONS || '').trim().toLowerCase();
  if (mode === 'user') return { isAdmin: false, adminNames: ['Ann Lee', 'Tom Tran'] };
  if (mode === 'noname') return { isAdmin: false, adminNames: [] };
  if (mode === 'admin') return { isAdmin: true, adminNames: [] };
  return null;
}

/** "Jane Doe", "Jane Doe or John Roe", "Jane Doe, John Roe or another admin". */
export function adminList(names) {
  const shown = (names || []).slice(0, ADMINS_SHOWN);
  const more = (names || []).length > ADMINS_SHOWN;
  if (!shown.length) return '';
  if (more) return `${shown.join(', ')} or another admin`;
  return shown.length === 2 ? `${shown[0]} or ${shown[1]}` : shown[0];
}

/**
 * The alert's sentence. An admin is pointed at monday's banner; anyone else
 * is told who can approve, by name when the names could be read.
 */
export function permissionMessage({ isAdmin, adminNames, missing = { updates: true, files: false } }) {
  const lead =
    missing.files && missing.updates
      ? 'File columns, updates and mentions in templates require new permissions for this app.'
      : missing.files
        ? 'File columns in templates require new permissions for this app.'
        : 'Updates and mentions in templates require new permissions for this app.';
  if (isAdmin) return `${lead} Please review and approve them in the monday banner above.`;
  const who = adminList(adminNames);
  if (who) return `${lead} Please ask your account admin, ${who}, to approve them.`;
  return `${lead} Please ask an admin of your monday account to approve them.`;
}
